import { useDigitalTwin } from 'api/digitalTwin/digitalTwin.api'
import { BASE_SETTING_PRIORITIES, DEVIATION_SETTING_PRIORITIES } from 'api/digitalTwin/digitalTwinConstants'
import { useHasPermission } from 'helpers/global.helper/global.helper'
import React, { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { LoadingPlaceholderContainer } from 'ui/atoms'
import Card from 'ui/atoms/Card/Card'
import NewIcon from 'ui/atoms/NewIcon/NewIcon'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import { getAllPriorityLevels } from 'utils/digitalTwinSettings/digitalTwinSettingUtils'
import DigitalTwinSettingsView from 'views/DigitalTwinSettingsView/DigitalTwinSettingsView'

export default function DigitalTwinDeviationSettingsView(): ReactElement | null {
  const { t } = useTranslation()
  const { activeSystem } = useAuth()

  const systemPriotyLevels = activeSystem?.setting_priority_levels ?? []

  const standardPriorities = [...BASE_SETTING_PRIORITIES, ...DEVIATION_SETTING_PRIORITIES, ...systemPriotyLevels].map(
    (p) => p.priority_level
  )

  const primaryDigitalTwin = activeSystem?.primary_digital_twin
  const { data: digitalTwin, isLoading: loadingDigitalTwin } = useDigitalTwin(primaryDigitalTwin?.uid)

  const priorityLevels = digitalTwin && Array.from(getAllPriorityLevels(digitalTwin))

  const nonStandardPriorityLevels = (priorityLevels ?? [])
    .filter((priority) => !standardPriorities.includes(priority))
    .map((priority) => ({
      priority_level: priority,
      display_name: t('Custom prio {{priority}}', { priority }),
    }))
    .sort((a, b) => a.priority_level - b.priority_level) // Sort so lowest priority is first in the list

  const canEditPlanSettings = useHasPermission('change_plan_settings')
  const canEditModelSettings = useHasPermission('change_model_settings')
  const canEditHistoricSettings = useHasPermission('change_historic_plan_settings')
  const canEditHistoricModelSettings = useHasPermission('change_historic_model_settings')

  const permissions = {
    canEdit: canEditModelSettings && canEditPlanSettings,
    canEditHistoric: canEditHistoricModelSettings && canEditHistoricSettings,
  }

  let content: ReactElement

  if (loadingDigitalTwin) {
    content = <LoadingPlaceholderContainer height={200} />
  } else if (nonStandardPriorityLevels.length === 0) {
    content = (
      <div className="w-full flex items-center justify-center mt-8">
        <div className="text-sm italic text-gray-500">{t(`No hidden settings priority levels found for system`)}</div>
      </div>
    )
  } else {
    content = (
      <DigitalTwinSettingsView
        priorities={nonStandardPriorityLevels}
        showCalendar={true}
        title={t(`Other settings`)}
        calendarTitle={t(`Other settings - calendar`)}
        tableTitle={t(`Other settings - table`)}
        isBaseSettings={false}
        permissions={permissions}
      />
    )
  }

  return (
    <div>
      <Card>
        <div className="text-xl font-bold flex flex-row items-center">
          <div className="mr-1">
            <NewIcon icon="fas fa-info-circle" ariaLabel="Info" />
          </div>
          {`${t("What are 'other settings'?")}`}
        </div>
        <div className="text-sm italic">
          <p>
            {t(
              'This view shows settings with priorities that are not included in the base or deviation setting views.'
            )}
          </p>
          <p>{t('It is only intended for super users, not for regular users.')}</p>
        </div>
      </Card>
      {content}
    </div>
  )
}
